import * as THREE from 'three';
import type { AudioBuses } from './buses';

export type SfxName = 'shoot' | 'hit' | 'kill' | 'jump' | 'pickup' | 'hurt' | 'superReady' | 'superUse' | 'checkpoint' | 'interact' | 'pause' | 'enemyAttack' | 'rareDrop' | 'craft' | 'stash' | 'huntComplete';

interface SfxDef {
  freq: number;
  /** Portamento target — omit for a flat tone. */
  freqEnd?: number;
  duration: number; // seconds
  type: OscillatorType;
  /** Peak envelope gain (0..1) before the sfx bus's own volume is applied. */
  gain: number;
}

/**
 * Short, procedurally synthesized blips — a single oscillator + gain
 * envelope per call, no audio files at all. Matches Classic's own
 * hand-rolled `sfx()` idiom (no sample loading, zero download-budget cost
 * per §3's 30MB slice budget) — extended here with the distance/pan
 * handling `playSfx` does for a "positional" call.
 */
const SFX_DEFS: Record<SfxName, SfxDef> = {
  shoot: { freq: 880, freqEnd: 660, duration: 0.05, type: 'square', gain: 0.18 },
  hit: { freq: 180, duration: 0.06, type: 'triangle', gain: 0.35 },
  kill: { freq: 520, freqEnd: 140, duration: 0.16, type: 'sawtooth', gain: 0.4 },
  jump: { freq: 300, freqEnd: 560, duration: 0.12, type: 'square', gain: 0.25 },
  pickup: { freq: 740, freqEnd: 1180, duration: 0.09, type: 'square', gain: 0.22 },
  hurt: { freq: 140, freqEnd: 90, duration: 0.14, type: 'sawtooth', gain: 0.4 },
  superReady: { freq: 660, freqEnd: 1320, duration: 0.3, type: 'triangle', gain: 0.3 },
  superUse: { freq: 90, freqEnd: 45, duration: 0.35, type: 'sawtooth', gain: 0.45 },
  checkpoint: { freq: 520, freqEnd: 780, duration: 0.22, type: 'triangle', gain: 0.28 },
  interact: { freq: 440, duration: 0.05, type: 'square', gain: 0.15 },
  pause: { freq: 380, duration: 0.08, type: 'square', gain: 0.2 },
  enemyAttack: { freq: 220, freqEnd: 260, duration: 0.08, type: 'sawtooth', gain: 0.22 },
  // Wave L-4 (PLAN-CITY2 §6): the rare-drop "sting" — a brighter, longer rise
  // than 'pickup' (0.09s/740->1180hz) so an EPIC/LEGENDARY cosmetic reads as
  // a genuinely bigger moment, matching the beam VFX's own rarity scaling
  // (loot/pickup.ts).
  rareDrop: { freq: 520, freqEnd: 1560, duration: 0.4, type: 'triangle', gain: 0.4 },
  // Wave I (PLAN-CITY3 §5, I-1: "dedicated craft/stash/hunt cues — six
  // events share one blip today") — before these, handleInteract's craft
  // grant, both stash discoveries (hidden/laundromat), the rooftop vantage
  // grant, and the ordinary loot pickup all played this same flat 'pickup'
  // tone, plus registerHuntKill's own completion reused it too. Each of the
  // three below is deliberately distinct in TYPE (not just freq) from
  // 'pickup'/'checkpoint'/'rareDrop' so they don't just sound like louder
  // pickups.
  craft: { freq: 220, freqEnd: 440, duration: 0.18, type: 'square', gain: 0.32 }, // a heavier, mechanical rise — Scrap Armor/Riot Plating/Salvage Exchange
  stash: { freq: 420, freqEnd: 1050, duration: 0.22, type: 'sine', gain: 0.3 }, // a clean "found it" ding — hidden/laundromat stash, rooftop vantage
  huntComplete: { freq: 440, freqEnd: 1100, duration: 0.35, type: 'triangle', gain: 0.35 }, // a longer, more triumphant rise than checkpoint's
};

const POSITIONAL_MAX_DISTANCE = 22; // beyond this, a positional cue is fully inaudible — the call is skipped entirely, not just quiet
const POSITIONAL_MIN_DISTANCE = 3; // inside this, full volume — no falloff for anything essentially at the listener

/**
 * Plays `name` on the sfx bus. Omit `at`/`listenerAt` (or pass neither) for
 * a player-anchored cue (shoot/jump/pickup/super/pause — always full
 * volume, no falloff, since these happen essentially at the listener).
 * Pass BOTH for a positional cue (an enemy's hit/kill/attack — PLAN-CITY
 * §5's "positional sfx" requirement): linear gain falloff between the two
 * radii above, plus a simple left/right stereo pan from the source's XZ
 * side relative to the listener. This is a lightweight distance/pan model,
 * not full HRTF spatialization — appropriate for a third-person camera
 * where "roughly which side and how far" is the whole signal that matters.
 *
 * Never throws before the AudioContext exists (buses.context is null until
 * the first user gesture calls `AudioBuses.unlock()`) — a juice call must
 * never be able to break gameplay logic around it.
 */
export function playSfx(buses: AudioBuses, name: SfxName, at?: THREE.Vector3, listenerAt?: THREE.Vector3): void {
  const positional = !!(at && listenerAt);
  buses.lastSfxPlayed = { name, positional }; // test/verification seam — set unconditionally, even before unlock

  const ctx = buses.context;
  const bus = buses.sfxBus;
  if (!ctx || !bus) return;

  const def = SFX_DEFS[name];
  let peakGain = def.gain;
  let pan = 0;
  if (at && listenerAt) {
    const dx = at.x - listenerAt.x;
    const dz = at.z - listenerAt.z;
    const dist = Math.hypot(dx, dz);
    if (dist >= POSITIONAL_MAX_DISTANCE) return; // fully inaudible — skip building any nodes at all
    const t = THREE.MathUtils.clamp((dist - POSITIONAL_MIN_DISTANCE) / (POSITIONAL_MAX_DISTANCE - POSITIONAL_MIN_DISTANCE), 0, 1);
    peakGain *= 1 - t;
    pan = THREE.MathUtils.clamp(dx / POSITIONAL_MAX_DISTANCE, -1, 1);
  }

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = def.type;
  osc.frequency.setValueAtTime(def.freq, now);
  if (def.freqEnd !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, def.freqEnd), now + def.duration);

  const env = ctx.createGain();
  env.gain.setValueAtTime(peakGain, now);
  env.gain.exponentialRampToValueAtTime(0.001, now + def.duration); // exponential ramps can't target exactly 0

  let panner: StereoPannerNode | null = null;
  if (pan !== 0 && ctx.createStereoPanner) {
    panner = ctx.createStereoPanner();
    panner.pan.value = pan;
  }
  osc.connect(env);
  if (panner) {
    env.connect(panner).connect(bus);
  } else {
    env.connect(bus);
  }

  osc.start(now);
  osc.stop(now + def.duration + 0.02);
  osc.onended = () => {
    osc.disconnect();
    env.disconnect();
    panner?.disconnect();
  };
}

const ARPEGGIO_BASE_FREQ = 660;
const ARPEGGIO_STEP = 1.2599; // 2^(4/12) — a rising major-third-ish step per note, purely for a "climbing" feel, not a real musical scale
const ARPEGGIO_NOTE_GAP = 0.045; // seconds between notes
const ARPEGGIO_NOTE_DURATION = 0.08;
const ARPEGGIO_MAX_NOTES = 4;

/**
 * Wave I (I-1): "rising arpeggio pickup sfx across a batch" — replaces the
 * old flat `playSfx(buses,'pickup')` call at the loot-collection site with a
 * short ascending run whose note COUNT scales with how many non-cosmetic
 * drops landed this tick (capped at `ARPEGGIO_MAX_NOTES` — a 12-coin
 * super-blast payout doesn't need 12 audible notes to read as "a big
 * pickup," and an unbounded note count risks audibly overlapping the NEXT
 * tick's own cue). Deliberately a separate function rather than a
 * `playSfx('pickup', {count})` overload — `playSfx` schedules exactly ONE
 * oscillator per call; scheduling several needs its own small loop, and
 * duplicating ~10 lines here is lower-risk than reshaping the well-reviewed
 * single-tone function every other cue in this file still depends on.
 */
export function playPickupArpeggio(buses: AudioBuses, count: number): void {
  buses.lastSfxPlayed = { name: 'pickup', positional: false }; // test/verification seam — reported under the existing 'pickup' name, same cue conceptually, just richer
  const ctx = buses.context;
  const bus = buses.sfxBus;
  if (!ctx || !bus) return;
  const notes = Math.min(ARPEGGIO_MAX_NOTES, Math.max(1, count));
  for (let i = 0; i < notes; i++) {
    const startAt = ctx.currentTime + i * ARPEGGIO_NOTE_GAP;
    const freq = ARPEGGIO_BASE_FREQ * Math.pow(ARPEGGIO_STEP, i);

    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, startAt);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.2, startAt);
    env.gain.exponentialRampToValueAtTime(0.001, startAt + ARPEGGIO_NOTE_DURATION);

    osc.connect(env).connect(bus);
    osc.start(startAt);
    osc.stop(startAt + ARPEGGIO_NOTE_DURATION + 0.02);
    osc.onended = () => {
      osc.disconnect();
      env.disconnect();
    };
  }
}
